import { useEffect, useState, useMemo, useRef } from "react";
import MapConfig from "../utils/map-config.json";
import { getDatasetsJson } from "../utils/DatasetsJson";
import { updateUrl, addLayerToMap } from "../utils/mapFunctions";
import Placeholder from "../img/image_placeholder.png";

const CatalogueControl = (props) => {
  const map = props.map;
  const DatasetsJson = getDatasetsJson();
  const layers = props.layers;
  const [inputValue, setInputValue] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const filteredByFilters = useMemo(() => {
    const filters = props.filters;

    if (!filters || Object.keys(filters).length === 0) return DatasetsJson;

    return DatasetsJson.filter(item =>
      Object.entries(filters).every(([key, values]) => {
        if (!Array.isArray(values) || values.length === 0) return true;

        if (item.hasOwnProperty(key)) {
          return values.includes(item[key]);
        }

        return item.datasets?.some(ds => values.includes(ds[key]));
      })
    );
  }, [props.filters, DatasetsJson]);

  const filteredData = useMemo(() => {
    if (!searchValue) return filteredByFilters;

    return filteredByFilters.filter(item =>
      item.title?.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [filteredByFilters, searchValue]);

  const loadedLayerIds = useRef(new Set());

  useEffect(() => {
    if (!map) return;

    const getLayer = (layerNum) => {
      for (const provider of DatasetsJson) {
        for (const dataset of provider.datasets || []) {
          const found = dataset.layers?.find(layer => layer.num_layer === layerNum);
          if (found) {
            return {...found, url: found.GetTile || dataset.GetMap || dataset.url, type: dataset.map_service};
          }
        }
      }
      return null;
    };

    const loadLayersFromUrl = async () => {
      const params = new URLSearchParams(window.location.search);
      let activeLayers = params.get("layer")?.split(",").map(Number) || [];

      if (activeLayers.length === 0) {
        activeLayers = MapConfig.DefaultLayers;
      }

      activeLayers.map(async layerNum => {
        if (loadedLayerIds.current.has(layerNum)) return;
        loadedLayerIds.current.add(layerNum);

        const item = getLayer(layerNum);

        if (!item) return;

        const layer = await addLayerToMap(map, layers, item.url, layerNum, item.layer_name, item);

        if (!layer) return;

        const order = activeLayers.indexOf(layerNum);
        props.addLayer(layer, item.url, layerNum, item.name_en, item.layer_name, 1, true, order);
        layers.forEach(l => l.layer.bringToFront());

        if (!params.get("layer")?.split(",").includes(String(layerNum))) {
          updateUrl(layerNum);
        }
      });
    };

    loadLayersFromUrl();
  }, [map, DatasetsJson, layers, props]);

  const handleSearchValue = () => {
    setSearchValue(inputValue.trim());
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchValue();
    }
  };

  const handleClearSearch = () => {
    setInputValue("");
    setSearchValue("");
  };

  const findLayers = (datasets) => {
    let layers = datasets.reduce((sum, dataset) => sum + (dataset.layers?.length || 0), 0);
    return layers;
  };

  const renderSelectedFilters = (selectedFilters) => {
    const filters = [];
    Object.entries(selectedFilters).forEach(([filterKey, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        values.forEach(value => filters.push({ filterKey, value }));
      }
    });

    const removeFilter = (filterKey, label) => {
      props.setFilters(prev => {
        const group = prev[filterKey];

        if (!Array.isArray(group) || !group.includes(label)) {
          return prev;
        }

        const updatedGroup = group.filter(item => item !== label);

        if (updatedGroup.length === 0) {
          const { [filterKey]: _, ...rest } = prev;
          return rest;
        }

        return {
          ...prev,
          [filterKey]: updatedGroup
        };
      });
    };

    return filters.map(({ filterKey, value }, index) => {
      const filterData = MapConfig.Filters.find(f => f.filter === filterKey);
      const labelObj = filterData?.labels.find(l => l.name === value);
      const label = labelObj?.label || value;

      return (
        <div className="catalogue-filters-item" key={index}>
          {label} <button className="icon-button widget-close" onClick={() => removeFilter(filterKey, labelObj?.name || value)} aria-label="Remove filter">
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>
      );
    });
  };

  const handleImageError = (e) => {
    e.target.src = Placeholder;
    e.target.onerror = null;
  };

  return (
    <>
      {props.active && (
        <div className="catalogue-container" style={{overflow: props.showDetail ? "hidden" : ""}}>
          <div className="catalogue-options">
            <div className="catalogue-options-counter">
              <b>{filteredData.length}</b> datasets
            </div>
            <button className="icon-button" onClick={() => props.setShowExternal(true)} aria-label="External services">
              <i className="fa-regular fa-square-plus"></i>
            </button>
            <button className="icon-button" onClick={() => props.setShowFilter(true)} aria-label="Filters">
              <i className="fa-solid fa-sliders"></i>
            </button>
          </div>
          <div className="catalogue-search">
            <div className="catalogue-search-input">
              <input
                type="text"
                placeholder="Search datasets"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)} 
                onKeyDown={handleKeyDown}
              />
              {inputValue.length > 0 &&
                <button className="catalogue-search-clear" onClick={handleClearSearch} aria-label="Clear search">
                  <i className="fa-solid fa-xmark"></i>
                </button>
              }
            </div>
            <button className="catalogue-search-button" onClick={handleSearchValue} aria-label="Search">
              <i className="fa-solid fa-magnifying-glass"></i>
            </button>
          </div>
          {props.filters && Object.keys(props.filters).length > 0 &&
            <div className="catalogue-filters">
              {renderSelectedFilters(props.filters)}
            </div>
          }
          <div className="catalogue-results">
            {filteredData.length === 0 && 
              <div className="catalogue-nodata">No results found</div>
            }
            {filteredData.map((item) => (
              <div className="catalogue-item" key={item.id}>
                <div className="catalogue-left">
                  <div className="catalogue-image">
                    <img src={item.thumbnail || Placeholder} alt="Dataset thumbnail"  style={{...(item.title.includes("UNESCO") && {backgroundColor: "white", objectFit: "contain"})}} onError={handleImageError}/>
                  </div>
                  <div className="catalogue-layers">
                    <i className="fa-solid fa-layer-group"></i>
                    <span>{findLayers(item.datasets)}</span>
                  </div>
                </div>
                <div className="catalogue-right">
                  <div className="catalogue-title">{item.title}</div>
                  <div className="catalogue-description">{item.description?.split("\n")[0]}</div>
                  <div className="catalogue-buttons">
                    <button className="ccl-button ccl-button--default ccl-button--default-neg" onClick={() => props.setShowDetail(item.id)} aria-label="Detail">
                      Details
                    </button>
                    <button
                      className="ccl-button ccl-button--default"
                      disabled={findLayers(item.datasets) === 0 && !item.metadata_url}
                      onClick={() => findLayers(item.datasets) === 0 ? window.open(item.metadata_url, '_blank') : props.setShowDatasets(item.id)}
                      aria-label={findLayers(item.datasets) === 0 ? "Use dataset" : "Add layers"}
                    >
                      {findLayers(item.datasets) === 0 ? "Use dataset" : "Add layers"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default CatalogueControl;
